/**
 * Main Application Component - OPTIMIZED WITH CODE SPLITTING
 *
 * Modular Route Architecture:
 * - Core routes:    Auth, dashboard, settings, system
 * - Retail routes:  Branches, sales, inventory, products
 * - Expense routes: Accounting, expenses, budgets, reports
 * - Wholesale:      Separate layout at /wholesale/*
 *
 * Loading: the retail and wholesale layouts (with all their pages) are separate chunks, so
 * the login page does not wait for the whole app. Both are fetched in the background as
 * soon as the app starts, and pages inside a module load eagerly, so moving around the
 * app never waits on a download.
 */
import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Eager imports (only critical components)
import PrivateRoute from './components/PrivateRoute';
import LoadingSpinner from './components/common/LoadingSpinner';

// Context Providers (keep eager - small and critical)
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { BranchProvider } from './contexts/BranchContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { FiscalPeriodProvider } from './contexts/FiscalPeriodContext';
import { ModuleProvider } from './contexts/ModuleContext';
import { PerformanceTaskProvider } from './contexts/PerformanceTaskContext';

import lazyWithReload from './utils/lazyWithReload';

// One chunk per module
const MainLayoutComponent = lazyWithReload(() => import('./components/layout/MainLayoutComponent'));
const ModuleSelectionPage = lazyWithReload(() => import('./pages/ModuleSelectionPage'));
const WholesaleLayout = lazyWithReload(() => import('./components/layout/WholesaleLayout'));

// Public routes
import LoginPage from './pages/LoginPage';
import DemoLoginPage from './pages/DemoLoginPage';
import SsoLoginPage from './pages/SsoLoginPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

function App() {
  useEffect(() => {
    MainLayoutComponent.preload();
    ModuleSelectionPage.preload();
    WholesaleLayout.preload();
  }, []);

  return (
    <ThemeProvider>
      <BrandingProvider>
        <AuthProvider>
          <ModuleProvider>
            <BranchProvider>
              <FiscalPeriodProvider>
                <PerformanceTaskProvider>
                  <Router>
                    <Suspense fallback={<LoadingSpinner message="Loading application..." />}>
                      <Routes>
                        {/* Public Routes - Lazy Loaded */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/demo-login" element={<DemoLoginPage />} />
                        <Route path="/sso" element={<SsoLoginPage />} />
                        <Route path="/change-password" element={<ChangePasswordPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />

                        {/* Module Selection */}
                        <Route
                          path="/select-module"
                          element={
                            <PrivateRoute>
                              <ModuleSelectionPage />
                            </PrivateRoute>
                          }
                        />

                        {/* Wholesale Module - Separate chunk */}
                        <Route
                          path="/wholesale/*"
                          element={
                            <PrivateRoute>
                              <WholesaleLayout />
                            </PrivateRoute>
                          }
                        />

                        {/* Retail / Main Module - Separate chunk */}
                        <Route
                          path="/*"
                          element={
                            <PrivateRoute>
                              <MainLayoutComponent />
                            </PrivateRoute>
                          }
                        />
                      </Routes>
                    </Suspense>

                    <ToastContainer
                      position="top-right"
                      autoClose={3000}
                      hideProgressBar={false}
                      newestOnTop
                      closeOnClick
                      rtl={false}
                      pauseOnFocusLoss
                      draggable
                      pauseOnHover
                      theme="light"
                    />
                  </Router>
                </PerformanceTaskProvider>
              </FiscalPeriodProvider>
            </BranchProvider>
          </ModuleProvider>
        </AuthProvider>
      </BrandingProvider>
    </ThemeProvider>
  );
}

export default App;
